import React, { useCallback, type ReactNode } from 'react';
import noop from 'lodash/noop';
import type { ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import type { ContentProps, TriggerProps as TriggerPropsPopup } from '@atlaskit/popup/types';
import { useOpenable } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-openable/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isNavigationProject, type NavigationContainer } from '../../common/types.tsx';
import { Menu } from './menu/Menu.tsx';

export type TriggerProps = TriggerPropsPopup & {
	onClick: () => void;
};

export type CreateViewProps = {
	renderTrigger: (props: TriggerProps) => ReactNode;
	navigationContainer: NavigationContainer;
	container?: string;
	sectionId?: string;
	onMenuItemClick?: (arg: { type: ViewKind; container?: string }) => void;
};

export const CreateViewMenu = ({
	container,
	sectionId,
	navigationContainer,
	renderTrigger,
	onMenuItemClick,
}: CreateViewProps): React.JSX.Element => {
	const { isOpen, onClose, onToggle } = useOpenable();

	const handleMenuItemClick = useCallback(
		({ type }: { type: ViewKind }) => {
			onClose();
			onMenuItemClick?.({ type, container });
		},
		[container, onClose, onMenuItemClick],
	);

	const isViewTemplateLibraryEnabled =
		expVal('view-template-library-m1-for-new-tenants', 'isEnabled', false) ||
		expVal('view-template-library-m1-for-existing-tenants', 'isEnabled', false);

	const content = __SERVER__
		? (_contentProps: ContentProps) => null
		: (contentProps: ContentProps) => (
				<Menu
					{...contentProps}
					container={container}
					sectionId={sectionId}
					navigationContainer={navigationContainer}
					onMenuItemClick={handleMenuItemClick}
					onPopupClose={isViewTemplateLibraryEnabled ? onClose : noop}
				/>
			);

	const trigger = (triggerProps: TriggerPropsPopup) =>
		renderTrigger({ ...triggerProps, onClick: onToggle });

	const messageId = isNavigationProject(navigationContainer)
		? `navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.popup.${navigationContainer.projectKey}`
		: `navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.popup.${navigationContainer.collectionUUID}`;

	return (
		<Popup
			shouldUseCaptureOnOutsideClick
			placement="auto-start"
			messageId={messageId}
			messageType="transactional"
			isOpen={isOpen}
			onClose={onClose}
			content={content}
			trigger={trigger}
		/>
	);
};
